import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { ControlContainer, FormControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { ILoanProduct } from '@libs/models/models';
import { environment } from '@libs/environment';
import { getValueNumber } from '@libs/helpers/helper.utils';
import { Loan } from '@libs/models/loan.model';
import { Options } from '@angular-slider/ngx-slider';
import { UnsubscribeService } from '@libs/shared/services/unsubscribe.service';
import { SESSION_STORAGE } from '@libs/shared/injections/window';

@Component({
  selector: 'app-calculate-widget',
  templateUrl: `./templates/${environment.group}/page.html`,
  styleUrls: [`./templates/${environment.group}/style.sass`],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UnsubscribeService],
})
export class CalculateWidgetComponent implements OnInit {
  @Input()
  public classes: string = '';

  @Input()
  public product: ILoanProduct;

  @Input()
  public disabled: boolean = false;

  @Input()
  public term: number;

  @Input()
  public amount: number;

  @Input()
  public type: 'primary' | 'secondary' | 'third' = 'primary';

  @Input()
  public loan: Loan;

  @Output()
  public readonly calculateChangeEvent: EventEmitter<{
    amount: number;
    term: number;
    interest: number;
  }> = new EventEmitter<{
    amount: number;
    term: number;
    interest: number;
  }>();
  public form: FormGroup;
  @Input()
  private formGroupName: string;
  private commonOptions: Options = {
    hidePointerLabels: true,
    hideLimitLabels: true,
    showSelectionBar: true,
  };

  constructor(
    private rootFormGroup: FormGroupDirective,
    @Inject(SESSION_STORAGE) private sessionStorage: Storage,
  ) {}

  get amountControl(): FormControl {
    return this.form.controls['amount'] as FormControl;
  }

  get termControl(): FormControl {
    return this.form.controls['term'] as FormControl;
  }

  public get settings(): string {
    return `sl-${this.type} ${this.classes}`;
  }

  get amountOptions(): Options {
    return {
      ...this.commonOptions,
      step: 100,
      floor: this.product.minAmount,
      ceil: this.product.maxAmount,
      disabled: this.amountControl.disabled,
    };
  }

  get termOptions(): Options {
    return {
      ...this.commonOptions,
      step: this.product.termStep,
      floor: this.product.minTerm,
      ceil: this.product.maxTerm,
      disabled: this.termControl.disabled,
    };
  }

  public ngOnInit(): void {
    this.form = this.rootFormGroup.control.get(this.formGroupName) as FormGroup;
  }

  public updateAmount(event: Event | number): void {
    if (this.form.controls['amount']?.disabled) {
      return;
    }
    this.amount = Math.max(this.product.minAmount, Math.min(getValueNumber(event), this.product.maxAmount));
    this.updateControl('amount', this.amount);
  }

  public updateTerm(event: Event | number): void {
    if (this.form.controls['term']?.disabled) {
      return;
    }
    this.term = Math.max(this.product.minTerm, Math.min(getValueNumber(event), this.product.maxTerm));
    this.updateControl('term', this.term);
  }

  private updateControl(controlName: string, value: number): void {
    this.sessionStorage.setItem(controlName, JSON.stringify(value));
    this.emitChangeEvent();
  }

  private emitChangeEvent(): void {
    this.calculateChangeEvent.emit({
      amount: this.amount,
      term: this.term,
      interest: this.product.interest,
    });
  }
}
